/**
 * CommissionTable — le percentuali e gli abbonamenti, letti dal backend.
 *
 * La percentuale **applicata** e i prezzi arrivano da `GET /api/billing/plans`,
 * che li prende dagli stessi moduli che creano i Price su Stripe e che
 * calcolano la commissione al checkout. Il motivo e' che la cifra promessa e
 * quella addebitata devono essere la stessa: quando non lo sono, la differenza
 * la paga l'utente, e la recensione la scrive il cliente, non noi.
 *
 * Anche le tariffe **barrate** arrivano dal backend (`increased_rates`, le
 * maggiorate della clausola 3.2.6): si modificano dal pannello Tariffe
 * dell'area staff, e un numero scritto a mano qui resterebbe quello di prima.
 * Il fallback in `lib/commissionRates.js` copre solo la richiesta fallita.
 *
 * Va sulla landing e sulla pagina di iscrizione perche' e' l'informazione che
 * manca a chi sta decidendo: "quanto mi costa" e "posso abbassarlo". Senza i
 * due numeri insieme, la domanda non ha risposta.
 *
 * `variant="compact"` e' per la pagina di iscrizione, dove lo spazio e' poco e
 * l'obiettivo e' una riga per piano. `variant="full"` e' per la landing.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n.jsx";
import { marketingHref } from "@/lib/localePath";
import { listCommissionPct, pctIt } from "@/lib/commissionRates";

const eur = (n) =>
  Number(n) % 1 === 0 ? `${Number(n)} €` : `${Number(n).toFixed(2).replace(".", ",")} €`;

function priceLabel(plan) {
  const variants = plan.variants || [];
  if (!variants.length) return null;
  if (variants.length === 1) return `${eur(variants[0].price_eur)}/mese`;
  const lo = Math.min(...variants.map((v) => v.price_eur));
  const hi = Math.max(...variants.map((v) => v.price_eur));
  return `da ${eur(lo)} a ${eur(hi)}/mese`;
}

export default function CommissionTable({ variant = "full", className = "" }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/billing/plans")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // Il fallback conta: se il backend non risponde la landing non deve
  // diventare una pagina con un buco. Ma i numeri qui sono le percentuali
  // storiche, e servono solo come testo — il link porta comunque alla pagina
  // vera, che non puo' mentire.
  const plans = data?.plans || [
    { tier: "base", name: IT ? "Senza abbonamento" : "No plan", commission_pct: 8, variants: [], buyable: false },
    { tier: "pro", name: "Hostaly Pro", commission_pct: 6, variants: [{ price_eur: 69 }], buyable: true },
    { tier: "enterprise", name: "Hostaly Enterprise", commission_pct: 4.5, variants: [{ price_eur: 249 }, { price_eur: 349 }], buyable: true },
  ];
  const ota = data?.ota_comparison;
  const compact = variant === "compact";
  const best = Math.min(...plans.map((p) => p.commission_pct));

  return (
    <div className={className} data-testid="commission-table">
      <div
        className={
          "grid gap-3 " +
          (compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-3")
        }
      >
        {plans.map((p) => {
          const isBest = p.commission_pct === best;
          const price = priceLabel(p);
          const listPct = listCommissionPct(p.tier, data?.increased_rates);
          return (
            <div
              key={p.tier}
              data-testid={`commission-plan-${p.tier}`}
              className={
                "rounded-xl border p-4 " +
                (isBest
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card")
              }
            >
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {p.name}
              </div>
              <div className="font-display text-3xl mt-1 flex items-baseline gap-2 flex-wrap">
                {listPct != null && (
                  <s
                    className="text-lg font-sans font-normal text-muted-foreground/70 decoration-2"
                    data-testid={`commission-list-${p.tier}`}
                  >
                    {pctIt(listPct)}
                  </s>
                )}
                <span data-testid={`commission-current-${p.tier}`}>{pctIt(p.commission_pct)}</span>
                <span className="text-xs font-sans text-muted-foreground">
                  {IT ? "a prenotazione" : "per booking"}
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {price || (IT ? "Nessun canone" : "No fee")}
              </div>
              {p.trial_days > 0 && (
                <div className="text-xs text-sage mt-1">
                  {IT ? `${p.trial_days} giorni di prova` : `${p.trial_days}-day trial`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Perche' il prezzo barrato esiste: non è un "prima" inventato, è la
          tariffa maggiorata che il regolamento applica a chi non espone i
          materiali dopo almeno tre avvisi scritti. Dirlo qui evita che il
          barrato sembri un prezzo pieno mai praticato. */}
      <p className="text-xs text-muted-foreground mt-3" data-testid="commission-list-note">
        {IT
          ? "Le tariffe barrate sono le tariffe maggiorate previste dal "
          : "Crossed-out rates are the increased rates set by the "}
        <Link to="/regolamento-affiliazione" className="text-primary hover:underline">
          {IT ? "regolamento di affiliazione" : "affiliation rules"}
        </Link>
        {IT
          ? " per le strutture che, dopo almeno tre avvisi scritti, non espongono i materiali Hostaly."
          : " for properties that, after at least three written notices, do not display the Hostaly materials."}
      </p>

      {!compact && ota && (
        <p className="text-sm text-muted-foreground mt-4" data-testid="commission-ota">
          {IT
            ? `Su Booking ed Expedia la commissione per l'host è del ${pctIt(ota.booking)}. Da noi parte dal ${pctIt(plans[0]?.commission_pct ?? 8)} e scende fino al ${pctIt(best)}.`
            : `Booking and Expedia charge hosts ${pctIt(ota.booking)}. Here it starts at ${pctIt(plans[0]?.commission_pct ?? 8)} and drops to ${pctIt(best)}.`}
        </p>
      )}

      <p className="text-xs text-muted-foreground mt-3">
        {IT
          ? "Nessun costo di iscrizione. I costi di gestione pagamento del circuiti carta non sono inclusi."
          : "No sign-up fee. Card processing fees are not included."}
        {failed && (
          <span className="ml-1">
            {IT
              ? " (Ultimi valori pubblicati: apri la pagina prezzi per quelli aggiornati.)"
              : " (Last published values: open the pricing page for the current ones.)"}
          </span>
        )}
      </p>

      <div className="mt-4">
        <Link
          to={marketingHref("pricing", lang)}
          className="text-sm font-medium text-primary hover:underline"
          data-testid="commission-table-link"
        >
          {IT ? "Vedi tutto cosa comprende ogni piano →" : "See what each plan includes →"}
        </Link>
      </div>
    </div>
  );
}
